import type { SyncQueueEntry } from './types';
import { getOperation } from './operationRegistry';

export function computeCollapsedIds(entries: SyncQueueEntry[]): number[] {
  const toDelete = new Set<number>();

  applyCancelPairs(entries, toDelete);
  applyDropIfParentDeleted(entries, toDelete);
  applyLastWins(entries, toDelete);

  return Array.from(toDelete);
}

function applyCancelPairs(entries: SyncQueueEntry[], toDelete: Set<number>): void {
  const pairGroups = new Map<string, SyncQueueEntry[]>();

  for (const entry of entries) {
    if (toDelete.has(entry.localId!)) continue;
    const op = getOperation(entry.type);
    if (op.collapseRule !== 'cancelPair' || !op.cancelPairType || !op.collapseKeyFn) continue;

    const key = `${op.collapseKeyFn(entry.payload)}::${[entry.type, op.cancelPairType].sort().join('+')}`;
    const group = pairGroups.get(key) ?? [];
    group.push(entry);
    pairGroups.set(key, group);
  }

  for (const [, group] of pairGroups) {
    const pairsToRemove = Math.floor(group.length / 2) * 2;
    for (let i = 0; i < pairsToRemove; i++) {
      toDelete.add(group[i].localId!);
    }
  }
}

function applyDropIfParentDeleted(entries: SyncQueueEntry[], toDelete: Set<number>): void {
  const deleteOps = entries.filter((e) => {
    if (toDelete.has(e.localId!)) return false;
    const op = getOperation(e.type);
    return op.isEntityDelete === true;
  });

  for (const deleteEntry of deleteOps) {
    const op = getOperation(deleteEntry.type);
    const deletedId = op.entityIdField ? deleteEntry.payload[op.entityIdField] : undefined;
    if (deletedId === undefined) continue;

    const matchingCreate = entries.find(
      (e) =>
        !toDelete.has(e.localId!) &&
        e.tempId === String(deletedId) &&
        getOperation(e.type).collapseRule === 'dropIfParentDeleted',
    );

    if (!matchingCreate) continue;

    toDelete.add(matchingCreate.localId!);
    toDelete.add(deleteEntry.localId!);

    for (const entry of entries) {
      if (toDelete.has(entry.localId!)) continue;
      const op = getOperation(entry.type);
      if (op.parentIdField && entry.payload[op.parentIdField] === deletedId) {
        toDelete.add(entry.localId!);
      }
    }
  }
}

function applyLastWins(entries: SyncQueueEntry[], toDelete: Set<number>): void {
  const groups = new Map<string, SyncQueueEntry[]>();

  for (const entry of entries) {
    if (toDelete.has(entry.localId!)) continue;
    const op = getOperation(entry.type);
    if (op.collapseRule !== 'lastWins' || !op.collapseKeyFn) continue;

    const key = `${entry.type}::${op.collapseKeyFn(entry.payload)}`;
    const group = groups.get(key) ?? [];
    group.push(entry);
    groups.set(key, group);
  }

  for (const [, group] of groups) {
    if (group.length <= 1) continue;
    for (let i = 0; i < group.length - 1; i++) {
      toDelete.add(group[i].localId!);
    }
  }
}
